import * as React from 'react'; // eslint-disable-line
import styled from 'styled-components';

import { COLOR, SPACING } from '../../packages/constants';
import { ChevronRight } from '../../packages/icons';
import { TrackingSettingsTheme } from '../tracking-settings/tracking-settings-enums';

export interface TreeCollapseProps {
    parentProp: React.ReactNode;
    children: React.ReactNode;
    dataId: string;
    parentClickable?: boolean;
    expand?: boolean;
    theme?: TrackingSettingsTheme;
    hideSplit?: boolean;
}

interface StyledChevronProps {
    isExpanded: boolean | undefined;
}

interface StyledButtonProps {
    dataId: string;
    parentClickable?: boolean;
}

interface StyledListProps {
    hideSplit?: boolean;
}

const StyledTreeCollapse = styled.ul``;

const StyledListItem = styled.li<StyledListProps>`
    position: relative;

    &::after {
        border-left: 1px solid ${COLOR.LEGACY.DATAGRID_BORDER};
        width: 1px;
        height: 100%;
        content: '';
        position: absolute;
        top: 0;
        right: calc(145px + ${SPACING.MEDIUM});
        display: ${(props) => (props.theme === TrackingSettingsTheme.DataGrid && !props.hideSplit ? 'block' : 'none')};
        z-index: 2;
    }
`;

const TreeParent = styled.div`
    display: flex;
    align-items: center;
    position: ${(props) => (props.theme === TrackingSettingsTheme.DataGrid ? 'sticky' : undefined)};
    top: ${(props) => (props.theme === TrackingSettingsTheme.DataGrid ? 0 : undefined)};
    z-index: ${(props) => (props.theme === TrackingSettingsTheme.DataGrid ? 1 : undefined)};
    background-color: ${(props) =>
        props.theme === TrackingSettingsTheme.DataGrid ? COLOR.LEGACY.DATAGRID_TH_BG : undefined};
    border-bottom: ${(props) =>
        props.theme === TrackingSettingsTheme.DataGrid ? `1px solid ${COLOR.LEGACY.DATAGRID_BORDER}` : 0};
    border-top: ${(props) =>
        props.theme === TrackingSettingsTheme.DataGrid ? `1px solid ${COLOR.LEGACY.DATAGRID_BORDER}` : 0};
    padding-left: ${(props) => (props.theme === TrackingSettingsTheme.DataGrid ? `${SPACING.MEDIUM}` : 0)};
`;

const TreeChildren = styled.div`
    margin-top: ${SPACING.MEDIUM};
`;

const StyledButton = styled.button.attrs<StyledButtonProps>(({ dataId }) => ({
    'data-test-id': dataId,
}))`
    border: 0;
    padding: 0;
    background-color: transparent;
    display: ${(props: StyledButtonProps): string => (props.parentClickable ? 'flex' : 'inline-block')};
    align-items: ${(props: StyledButtonProps): string => (props.parentClickable ? 'center' : '')};
    cursor: pointer;
`;

const StyledChevron = styled(ChevronRight)<StyledChevronProps>`
    width: 12px;
    height: 12px;
    fill: ${COLOR.MAIN.LABEL_TEXT};
    margin-right: ${SPACING.X_SMALL};
    transform: ${(props: StyledChevronProps): string => (props.isExpanded ? 'rotate(90deg)' : 'rotate(0)')};
`;

export const TreeCollapse = ({
    parentClickable,
    expand,
    parentProp,
    dataId,
    children,
    theme,
    hideSplit,
    ...props
}: TreeCollapseProps) => {
    const [isExpanded, setIsExpanded] = React.useState(true);

    React.useEffect(() => {
        setIsExpanded(expand || false);
    }, [expand]);

    return (
        <StyledTreeCollapse {...props}>
            <StyledListItem theme={theme} hideSplit={hideSplit}>
                {parentClickable ? (
                    <TreeParent theme={theme}>
                        <StyledButton
                            dataId={dataId}
                            parentClickable={parentClickable}
                            onClick={() => setIsExpanded(!isExpanded)}
                            aria-label={`${isExpanded ? 'Collapse' : 'Expand'} tree`}
                        >
                            <StyledChevron isExpanded={isExpanded} />
                            {parentProp}
                        </StyledButton>
                    </TreeParent>
                ) : (
                    <TreeParent theme={theme}>
                        <StyledButton
                            dataId={dataId}
                            onClick={() => setIsExpanded(!isExpanded)}
                            aria-label={`${isExpanded ? 'Collapse' : 'Expand'} tree`}
                        >
                            <StyledChevron isExpanded={isExpanded} />
                        </StyledButton>
                        {parentProp}
                    </TreeParent>
                )}
                {isExpanded && <TreeChildren>{children}</TreeChildren>}
            </StyledListItem>
        </StyledTreeCollapse>
    );
};
